// MobileEditorHeader — sticky Kopfzeile der mobilen Editor-Ansicht: Deckname
// (Inline-Umbenennen), Kartenzähler, Statistik-Knopf und das ⋮-Menü mit den
// seltenen Funktionen (Umbenennen, Export, Spalten, Ausgeblendete zeigen). Im
// Auswahl-Modus tritt an ihre Stelle die Auswahl-Kopfzeile (Zähler + Abbrechen).
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { BarChart3, Columns3, Eye, EyeOff, Lock, Pencil, Upload, X } from 'lucide-react';
import Badge from '../../../../ui/Badge';
import Input from '../../../../ui/Input';
import ContextMenu from '../../../../ui/ContextMenu';
import MobileMenuTrigger from './MobileMenuTrigger';
import { useCloseOnBack } from '../../../../ui/backLayer/useCloseOnBack';
import {
  MOBILE_MIN_COLUMNS,
  MOBILE_MAX_COLUMNS,
} from '../../../../hooks/useEditorPreferences';

const COLUMN_OPTIONS = Array.from(
  { length: MOBILE_MAX_COLUMNS - MOBILE_MIN_COLUMNS + 1 },
  (_, i) => MOBILE_MIN_COLUMNS + i
);

const MobileEditorHeader = forwardRef(function MobileEditorHeader({
  deckName,
  isPhysical = false,
  canEditDeck = false,
  cardCount = 0,
  targetCount = null,
  onRename,
  onOpenStats,
  onExport,
  columnCount,
  onColumnCountChange,
  showExcluded = true,
  onToggleShowExcluded,
  selectionMode = false,
  selectedCount = 0,
  onCancelSelection,
  onShowSelection,
}, ref) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(deckName || '');
  const inputRef = useRef(null);
  const skipCommitRef = useRef(false);
  const focusFieldOnMenuCloseRef = useRef(false);

  // Abbrechen gewinnt gegen Speichern-beim-Verlassen: der Vermerk steht, bevor das Feld
  // verschwindet, sonst schreibt der Blur-Weg den halben Namen fest.
  useCloseOnBack(isRenaming, () => {
    skipCommitRef.current = true;
    setIsRenaming(false);
  });

  useEffect(() => {
    setDraft(deckName || '');
    setIsRenaming(false);
  }, [deckName]);

  const startRename = useCallback(() => {
    if (!canEditDeck) return;
    setIsRenaming(true);
    focusFieldOnMenuCloseRef.current = true;
  }, [canEditDeck]);

  // Den Fokus setzt erst das Schließen des Menüs: Radix legt ihn im
  // FocusScope-Cleanup auf den ⋮-Trigger zurück und wäre mit jedem eigenen
  // Fokus-Versuch aus `onSelect` heraus später dran — das Feld verlöre den Fokus
  // und auf dem Handy erschiene keine Tastatur.
  const handleMenuCloseAutoFocus = useCallback((event) => {
    if (!focusFieldOnMenuCloseRef.current) return;
    focusFieldOnMenuCloseRef.current = false;
    event.preventDefault();
    const focusField = () => {
      inputRef.current?.focus?.();
      inputRef.current?.select?.();
    };
    if (inputRef.current) focusField();
    else requestAnimationFrame(focusField);
  }, []);

  const commitRename = useCallback(() => {
    setIsRenaming(false);
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      setDraft(deckName || '');
      return;
    }
    const trimmed = (draft || '').trim();
    if (!trimmed || trimmed === (deckName || '').trim()) {
      setDraft(deckName || '');
      return;
    }
    onRename?.(trimmed);
  }, [draft, deckName, onRename]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      skipCommitRef.current = true;
      event.currentTarget.blur();
    }
  }, []);

  const counter = targetCount ? `${cardCount}/${targetCount}` : `${cardCount} Karten`;

  // Dieselbe Wurzel-Klasse wie die Editor-Kopfzeile: der Sticky-Versatz der
  // Kategorie-Köpfe wird an diesem Element gemessen und darf beim Wechsel in den
  // Auswahl-Modus nicht springen.
  if (selectionMode) {
    return (
      <div className="mobile-editor-header" ref={ref}>
        <button
          type="button"
          className="mobile-editor-header__selection-count"
          onClick={onShowSelection}
        >
          {selectedCount} ausgewählt
        </button>
        <button
          type="button"
          className="btn-secondary mobile-editor-header__cancel"
          onClick={onCancelSelection}
        >
          <X size={16} aria-hidden /> Abbrechen
        </button>
      </div>
    );
  }

  return (
    <div className="mobile-editor-header" ref={ref}>
      {isPhysical && (
        <span
          className="mobile-editor-header__lock"
          title="Physische Decks sind im Editor weitgehend schreibgeschützt."
        >
          <Badge tone="pending">
            <Lock size={11} aria-hidden />
          </Badge>
        </span>
      )}

      {isRenaming ? (
        <span className="mobile-editor-header__name-input">
          <Input
            ref={inputRef}
            size="sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={handleKeyDown}
            aria-label="Deckname bearbeiten"
          />
        </span>
      ) : (
        <span className="mobile-editor-header__name" title={deckName}>
          {deckName || 'Unbenanntes Deck'}
        </span>
      )}

      <span className="mobile-editor-header__count">{counter}</span>

      <button
        type="button"
        className="btn-icon mobile-editor-header__btn"
        aria-label="Statistik anzeigen"
        title="Statistik anzeigen"
        onClick={() => onOpenStats?.()}
      >
        <BarChart3 aria-hidden />
      </button>

      <ContextMenu
        trigger={<MobileMenuTrigger label="Deck-Aktionen" />}
        onCloseAutoFocus={handleMenuCloseAutoFocus}
      >
        {canEditDeck && (
          <ContextMenu.Item icon={Pencil} onSelect={startRename}>Umbenennen</ContextMenu.Item>
        )}
        <ContextMenu.Item icon={Upload} onSelect={onExport}>Export</ContextMenu.Item>
        <ContextMenu.Submenu icon={Columns3} label="Spalten">
          {COLUMN_OPTIONS.map((count) => (
            <ContextMenu.Item key={count} checked={count === columnCount} onSelect={() => onColumnCountChange?.(count)}>
              {count === 1 ? '1 Spalte' : `${count} Spalten`}
            </ContextMenu.Item>
          ))}
        </ContextMenu.Submenu>
        <ContextMenu.Separator />
        {/* Der Zustand steckt im Icon statt in einem Häkchen: alle Einträge dieser
            Ebene belegen den Icon-Platz, ein Häkchen daneben markierte die Zeile
            doppelt. Im Spalten-Untermenü gibt es keine Icons — dort trägt das
            Häkchen den Zustand. */}
        <ContextMenu.Item
          icon={showExcluded ? Eye : EyeOff}
          keepOpen
          onSelect={onToggleShowExcluded}
        >
          Ausgeblendete Kategorien zeigen
        </ContextMenu.Item>
      </ContextMenu>
    </div>
  );
});

export default MobileEditorHeader;
